import {Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {PublisherAutocompleteComponent} from './publisher-autocomplete.component';
import {Publisher, PublisherStatusColorMap} from '../../domain/publisher.model';
import {PublisherListFilter, PublishersService} from '../../services/publishers.service';
import { Observable } from 'rxjs';

@Component({
    selector: 'ctv-publisher-autocomplete-chips',
    template: `
      <ctv-publisher-autocomplete #pc
        [networkIds]="networkIds"
        (publisherSelected)="onItemSelected($event)"
        [formFieldClass]="formFieldClass"
        [required]="required"
        [label]="placeholder"
        [placeholder]="placeholder"
        [vbxOnly]="vbxOnly"
        [floatLabel]="floatLabel"
        [formFieldAppearance]="formFieldAppearance ? formFieldAppearance : ''"
        [showOptionCheckbox]="true"
        [selectedOptionIds]="selectedOptionIds">
      </ctv-publisher-autocomplete>
      <div class="publisher-chips" [class.mb-3]="items.length > 0" [hidden]="items.length < 1">
        @for (item of items; track item.id) {
          <div class="publisher-chips__chip">
            <small class="fas fa-circle publisher-chips__status"
              [ngClass]="'text-' + PublisherStatusColorMap[item.status]"
              aria-hidden="true"></small>
            <span class="publisher-chips__name">{{item.name}}</span>
            <span *csHasNetworkPermission>
              @if (item.network) {
                <span class="badge badge-primary publisher-chips__network">N</span>
              }
            </span>
            <button type="button"
              class="publisher-chips__remove"
              (click)="remove(item)"
              [attr.aria-label]="'Remove ' + item.name">
              <i class="fas fa-times-circle text-danger" aria-hidden="true"></i>
            </button>
          </div>
        }
      </div>
      `,
    styles: [`
      :host {
        display: block;
        max-width: 100%;
        min-width: 0;
      }

      .publisher-chips {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
        margin-top: 8px;
        max-width: 100%;
        min-width: 0;
      }

      .publisher-chips__chip {
        display: inline-flex;
        flex: 0 1 auto;
        align-items: center;
        width: auto;
        max-width: 100%;
        min-width: 0;
        overflow: hidden;
        min-height: 32px;
        padding: 6px 10px;
        background: #f8fafc;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        box-sizing: border-box;
        line-height: 1.25;
      }

      .publisher-chips__status {
        display: inline-flex;
        flex: 0 0 auto;
        align-items: center;
        justify-content: center;
        width: 8px;
        height: 8px;
        margin-right: 8px;
        font-size: 8px;
        line-height: 1;
      }

      .publisher-chips__name {
        display: block;
        flex: 1 1 auto;
        margin: 0;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .publisher-chips__network {
        margin-left: 6px;
      }

      .publisher-chips__remove {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 18px;
        height: 18px;
        margin-left: 8px;
        padding: 0;
        border: 0;
        background: transparent;
        cursor: pointer;
        line-height: 1;
      }

      .publisher-chips__remove .fa-times-circle {
        font-size: 14px;
        line-height: 1;
      }
    `],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PublisherAutocompleteChipsComponent implements OnInit {
  @ViewChild(PublisherAutocompleteComponent, {static: true}) pc: PublisherAutocompleteComponent;

  /** Pass eventsSubjectNetwork = new Subject<string[]>(); as Observable into it and then call this.eventsSubjectNetwork.next(networkIds) to filter pubs for that given network*/
  @Input() networkIdsChanged: Observable<string[]>;

  /** Pass clearFilterItems = new Subject<void>(); as Observable into it and then call clearFilterItems.next() to clear all the items*/
  @Input() clearItems: Observable<void>;

  @Input()
  public placeholder: string;

  @Input()
  public stacked: false;

  @Input()
  vbxOnly = false;

  @Input()
  public publisherIds: string[];

  @Input()
  public floatLabel: string;

  @Input()
  public formFieldClass: string;

  @Input()
  public formFieldAppearance: string;

  /** Hold list of networkIds to filter pubs only by given networks.*/
  @Input() public networkIds: string[];

  @Input()
  required = false;

  @Output()
  publisherSelectionChange = new EventEmitter<Publisher[]>();

  items: Publisher[] = [];

  PublisherStatusColorMap = PublisherStatusColorMap;

  get selectedOptionIds(): Array<string | number> {
    return (this.items || []).map((item) => item.id);
  }

  constructor(private service: PublishersService) {
  }

  ngOnInit(): void {
    if (this.publisherIds && this.publisherIds.length) {
      //On parent component reload if there is single preselected item, it will actually be passed as string instead of a list
      if (typeof this.publisherIds === 'string') {
        this.publisherIds = [this.publisherIds];
      }
      //Fetch only preselected publishers
      this.service.listPublishers({ids: this.publisherIds} as PublisherListFilter).subscribe(publishers => {
        publishers.content.map(pub => {this.items.push(pub);});
        this.pc.updateFilteredPublishers(this.items);
      });
    }

    this.networkIdsChanged?.subscribe((result) => this.updatePublisherIdsChips(result));
    this.clearItems?.subscribe(() => this.clearFilter());
  }

  /** Remove items that are not part of given network/s. */
  updatePublisherIdsChips(networks: string[]) {
    this.items = this.items.filter(x => networks.includes(x.network?.id));
    this.pc.updateFilteredPublishers(this.items);
  }

  onItemSelected($event: Publisher) {
    if ($event) {
      this.items.push($event);
      this.publisherSelectionChange.emit(this.items);
      this.pc.resetInput();
      this.pc.updateFilteredPublishers(this.items);
    }
  }

  remove(p: Publisher) {
    this.items.splice(this.items.indexOf(p), 1);
    this.publisherSelectionChange.emit(this.items);
    this.pc.updateFilteredPublishers(this.items);
  }

  /** Clear all items from filter. */
  clearFilter(){
    this.items = [];
    this.pc.updateFilteredPublishers(this.items);
  }

  disableFilter(disable: boolean) {
    if (disable) {
      this.pc.disable();
    }
    else {
      this.pc.enable();
    }
  }
}
